<template>
  <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
    <p class="text-sm text-slate-700 mt-1">
    Allow edit access privileges to the following lists:
    </p>

    <div v-if="canSeeAnyField" class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div v-if="canSeeGrvField" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">GRV Number</label>
        <input
          v-model="grvNumber"
          type="text"
          placeholder="Enter GRV Number"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
        />
      </div>

      <div v-if="canSeeSivField" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">SIV Number</label>
        <input
          v-model="sivNumber"
          type="text"
          placeholder="Enter SIV Number"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
        />
      </div>

      <div v-if="canSeeNsField" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">NS Number</label>
        <input
          v-model="nsNumber"
          type="text"
          placeholder="Enter NS Number"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
        />
      </div>
    </div>

    <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Production</label>
        <select
          v-model="selectedProduction"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
        >
          <option value="" disabled>Select</option>
          <option value="Daily Inventory">Daily Inventory</option>
          <option value="Daily products">Daily products</option>
          <option value="Daily Transaction">Daily Transaction</option>
          <option value="Material Inventory">Material Inventory</option>
          <option value="Consumption">Consumption</option>
          <option value="Debone Inventory">Debone Inventory</option>
          <option value="Debone Controls">Debone Controls</option>
          <option value="Central Inventory">Central Inventory</option>
          <option value="Central Product Distribution">Central Product Distribution</option>
          <option value="Central Item Controls">Central Item Controls</option>
        </select>
      </div>

      <div v-if="!isCentralKitchenProductionSelected" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
          Department
          <span v-if="isDeboneProductionSelected" class="text-xs font-normal text-slate-500">(optional)</span>
        </label>
        <select
          v-model="selectedDepartment"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
        >
          <option v-if="!isDeboneProductionSelected" value="" disabled>Select Department</option>
          <option v-if="isDeboneProductionSelected" :value="NO_DEPARTMENT">No Department</option>
          <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Calendar</label>
        <input
          v-model="calendarDate"
          type="date"
          @keydown.prevent
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
        >
      </div>
    </div>

    <div v-if="canSeeAnyField" class="mt-4">
      <button
        type="button"
        @click="handleFind"
        class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95"
      >
        <span v-if="!isFinding">Find</span>
        <span v-else>Finding...</span>
      </button>
    </div>

    <!-- GRV Results -->
    <div v-if="grvRows.length > 0" class="mt-6">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Grv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Pr No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Supplier</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <!-- <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th> -->
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Tr. Date</th>
              <!-- <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total Price</th> -->
              <!-- <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Expiry</th> -->
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in grvRows" :key="`${r.grvNo}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.grvNo }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.prNoSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.prNo || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">
                <input type="checkbox" v-model="r.supplierSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.supplier || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.categorySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.category || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold text-[#0d4169]">
                <input type="checkbox" v-model="r.itemSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.item || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.qtySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qty }}</span>
              </td>
              <!-- <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.volumeSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.volume }}</span>
              </td> -->
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.transactionDateSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.transactionDate || '-' }}</span>
              </td>
              <!-- <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.expirySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.expiry }}</span>
              </td> -->
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.taxableSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="status-pill text-[0.6rem]" :class="r.taxable ? 'active' : 'inactive'">
                  {{ r.taxable ? 'Yes' : 'No' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex justify-end">
        <button
          @click="handleAllowEditAccess"
          :disabled="isSubmittingEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingEditAccess ? 'Saving...' : 'Allow GRV Edit Access' }}</span>
        </button>
      </div>
    </div>

    <!-- SIV Results -->
    <div v-if="sivRows.length > 0" class="mt-10">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Siv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Tr. Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Department</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <!-- <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th> -->
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in sivRows" :key="`${r.sivNo}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.sivNo }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.transactionDateSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.transactionDate || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">
                <input type="checkbox" v-model="r.departmentSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.department || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.categorySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.category || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold text-[#0d4169]">
                <input type="checkbox" v-model="r.itemSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.item || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.qtySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qty }}</span>
              </td>
              <!-- <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.volumeSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.volume }}</span>
              </td> -->
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.remarkSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.remark || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex justify-end">
        <button
          @click="handleAllowSivEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 inline-flex items-center gap-2"
        >
          Allow SIV Edit Access
        </button>
      </div>
    </div>

    <!-- Non-Stockable Results -->
    <div v-if="nsRows.length > 0" class="mt-10">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">NS No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Pr No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Supplier</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Department</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Tr. Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Taxable</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in nsRows" :key="`${r.nsNo}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.nsNo }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.prNoSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.prNo || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">
                <input type="checkbox" v-model="r.supplierSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.supplier || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">
                <input type="checkbox" v-model="r.departmentSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.department || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.categorySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.category || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold text-[#0d4169]">
                <input type="checkbox" v-model="r.itemSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.item || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.qtySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qty }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.volumeSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.volume }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.transactionDateSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.transactionDate || '-' }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.taxableSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="status-pill text-[0.6rem]" :class="r.taxable ? 'active' : 'inactive'">
                  {{ r.taxable ? 'Yes' : 'No' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex justify-end">
        <button
          @click="handleAllowNsEditAccess"
          :disabled="isSubmittingEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingEditAccess ? 'Saving...' : 'Allow NS Edit Access' }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="hasSearched && grvRows.length === 0 && sivRows.length === 0 && nsRows.length === 0"
      class="mt-6 text-sm text-slate-500"
    >
      No items found.
    </div>

    <!-- Production: Daily Inventory Results (moved below all other tables) -->
    <div v-if="selectedProduction === 'Daily Inventory'" class="mt-10">
      <div v-if="productionError" class="text-sm text-red-600 font-semibold mb-3">
        {{ productionError }}
      </div>

      <div v-if="isLoadingProduction" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="dailyInventoryRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Ingredient Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M st</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M pro(f.b)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty Pack</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty Single</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.Price Pack</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.Price Single</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">T.Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in dailyInventoryRows" :key="r.id ?? `${r.ingredientName}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-2 py-1.5 text-[0.7rem] font-bold"
                :class="r.type === 'product' ? 'text-emerald-700' : 'text-[#0d4169]'"
              >
                {{ r.ingredientName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uomStore || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uomFB || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtyPackSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtyPack }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.volumeSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.volume }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtySingleSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtySingle }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ money(r.uPricePack) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ money(r.uPriceSingle) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-black">{{ money(r.tPrice) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.remarkSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.remark || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingProduction && dailyInventoryRows.length > 0" class="mt-4 flex justify-end">
        <button
          @click="handleAllowDailyInventoryEditAccess"
          :disabled="isSubmittingDailyInvEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingDailyInvEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingDailyInvEditAccess ? 'Saving...' : 'Allow Daily Inventory Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingProduction && dailyInventoryShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No daily inventory has been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingProduction" class="text-sm text-slate-500">
        Select Department and Calendar to load Daily Inventory.
      </div>
    </div>

    <!-- Production: Central Inventory Results -->
    <div v-if="selectedProduction === 'Central Inventory'" class="mt-10">
      <div v-if="centralInventoryError" class="text-sm text-red-600 font-semibold mb-3">
        {{ centralInventoryError }}
      </div>

      <div v-if="isLoadingCentralInventory" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="centralInventoryRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Ingredient Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M st</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M pro(f.b)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty Pack</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty Single</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">T.Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in centralInventoryRows"
              :key="r.centralInventoryId ?? `${r.ingredientName}-${i}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-2 py-1.5 text-[0.7rem] font-bold"
                :class="r.type === 'product' ? 'text-emerald-700' : 'text-[#0d4169]'"
              >
                {{ r.ingredientName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uomStore || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uomFB || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtyPackSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtyPack }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.volumeSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.volume }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtySingleSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtySingle }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-black">{{ money(r.tPrice) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.remarkSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.remark || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingCentralInventory && centralInventoryRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowCentralInventoryEditAccess"
          :disabled="isSubmittingCentralInvEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingCentralInvEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingCentralInvEditAccess ? 'Saving...' : 'Allow Central Inventory Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingCentralInventory && centralInventoryShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No central inventory has been saved for this date.
      </div>
      <div v-else-if="!isLoadingCentralInventory" class="text-sm text-slate-500">
        Select Calendar to load Central Inventory.
      </div>
    </div>

    <!-- Production: Central Product Distribution Results -->
    <div v-if="selectedProduction === 'Central Product Distribution'" class="mt-10">
      <div v-if="productDistributionError" class="text-sm text-red-600 font-semibold mb-3">
        {{ productDistributionError }}
      </div>

      <div v-if="isLoadingProductDistribution" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="productDistributionRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Product Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total Produced</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Transfer Issu (D)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Transfer Issu (T)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in productDistributionRows"
              :key="r.productDistributionId ?? `${r.productName}-${i}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] font-bold text-emerald-700">
                {{ r.productName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.totalProducedSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.totalProduced }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.transferIssueDSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ formatProductDistributionTransferD(r) }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-black">{{ formatQty(r.transOutTotal) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingProductDistribution && productDistributionRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowProductDistributionEditAccess"
          :disabled="isSubmittingProductDistributionEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingProductDistributionEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingProductDistributionEditAccess ? 'Saving...' : 'Allow Central Product Distribution Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingProductDistribution && productDistributionShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No central product distribution has been saved for this date.
      </div>
      <div v-else-if="!isLoadingProductDistribution" class="text-sm text-slate-500">
        Select Calendar to load Central Product Distribution.
      </div>
    </div>

    <!-- Production: Central Item Controls Results -->
    <div v-if="selectedProduction === 'Central Item Controls'" class="mt-10">
      <div v-if="centralItemControlError" class="text-sm text-red-600 font-semibold mb-3">
        {{ centralItemControlError }}
      </div>

      <div v-if="isLoadingCentralItemControl" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="centralItemControlRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table min-w-max">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Ingredient Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Wastage</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Transfer Issu (D)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Spoiled By Items</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Other/Catering</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in centralItemControlRows"
              :key="r.centralItemControlId ?? `${r.ingredientName}-${i}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
              :class="r.type === 'product' ? 'bg-emerald-50/40' : ''"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-2 py-1.5 text-[0.7rem] font-bold"
                :class="r.type === 'product' ? 'text-emerald-800' : 'text-[#0d4169]'"
              >
                {{ r.ingredientName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold text-red-700">
                <input type="checkbox" v-model="r.wastageSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ formatQty(r.wastage) }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">
                <input type="checkbox" v-model="r.transferIssueDSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ formatCentralItemControlTransferD(r) }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold text-red-700">
                <input type="checkbox" v-model="r.spoiledItemSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ formatQty(r.spoiledItem) }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold text-red-700">
                <input type="checkbox" v-model="r.cateringSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ formatQty(r.catering) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingCentralItemControl && centralItemControlRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowCentralItemControlEditAccess"
          :disabled="isSubmittingCentralItemControlEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingCentralItemControlEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingCentralItemControlEditAccess ? 'Saving...' : 'Allow Central Item Controls Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingCentralItemControl && centralItemControlShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No central item controls have been saved for this date.
      </div>
      <div v-else-if="!isLoadingCentralItemControl" class="text-sm text-slate-500">
        Select Calendar to load Central Item Controls.
      </div>
    </div>

    <!-- Production: Daily Products Results -->
    <div v-if="selectedProduction === 'Daily products'" class="mt-10">
      <div v-if="dailyProductsError" class="text-sm text-red-600 font-semibold mb-3">
        {{ dailyProductsError }}
      </div>

      <div v-if="isLoadingDailyProducts" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="dailyProductsRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Product Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Quantity</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in dailyProductsRows" :key="r.productId ?? `${r.productName}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] font-bold text-[#0d4169]">{{ r.productName || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uom || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.quantitySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.quantity }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingDailyProducts && dailyProductsRows.length > 0" class="mt-4 flex justify-end">
        <button
          @click="handleAllowDailyProductsEditAccess"
          :disabled="isSubmittingDailyProdEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingDailyProdEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingDailyProdEditAccess ? 'Saving...' : 'Allow Daily Products Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingDailyProducts && dailyProductsShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No daily products have been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingDailyProducts" class="text-sm text-slate-500">
        Select Department and Calendar to load Daily Products.
      </div>
    </div>

    <!-- Production: Daily Transaction Results -->
    <div v-if="selectedProduction === 'Daily Transaction'" class="mt-10">
      <div v-if="dailyTransactionError" class="text-sm text-red-600 font-semibold mb-3">
        {{ dailyTransactionError }}
      </div>

      <div v-if="isLoadingDailyTransaction" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="dailyTransactionRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Menu Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Sales (Man)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Sales (Sys)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Mgmt</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Room</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Promo</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Co. Cost</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Spoilage</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Other</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in dailyTransactionRows" :key="r.menuId ?? `${r.item}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] font-bold text-[#0d4169]">{{ r.item || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.salesManualSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.salesManual }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.salesSystem }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <!-- <input type="checkbox" v-model="r.managementSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" /> -->
                <span class="align-middle">{{ r.management }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.roomSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.room }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.promotionSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.promotion }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.companyCostSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.companyCost }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.spoilageSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.spoilage }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.otherSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.other }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-black">{{ r.total }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingDailyTransaction && dailyTransactionRows.length > 0" class="mt-4 flex justify-end">
        <button
          @click="handleAllowDailyTransactionEditAccess"
          :disabled="isSubmittingDailyTransEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingDailyTransEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingDailyTransEditAccess ? 'Saving...' : 'Allow Daily Transaction Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingDailyTransaction && dailyTransactionShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No daily transaction has been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingDailyTransaction" class="text-sm text-slate-500">
        Select Department and Calendar to load Daily Transaction.
      </div>
    </div>

    <!-- Production: Consumption (saved consumption_records only) -->
    <div v-if="selectedProduction === 'Consumption'" class="mt-10">
      <div v-if="consumptionError" class="text-sm text-red-600 font-semibold mb-3">
        {{ consumptionError }}
      </div>

      <div v-if="isLoadingConsumption" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="consumptionRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table min-w-max">
          <thead>
            <tr>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">No.</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Ingredient Name</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Bbf/Bignin/</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">R.S. Quantity</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">R.N.S. Quantity</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Product</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-emerald-800 whitespace-nowrap bg-emerald-50/50">Transfer Recived (D)</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-emerald-800 whitespace-nowrap bg-emerald-50/50">Transfer Recived (T)</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Wastage</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap bg-slate-100">Total On Hand</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Sales Manwale</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Sales System</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Managment</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Room</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Promotion</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Company Cost</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Spoilage</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Other</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-blue-800 whitespace-nowrap bg-blue-50/50">Transfer Issu (D)</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-blue-800 whitespace-nowrap bg-blue-50/50">Transfer Issu (T)</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Spoiled By Items</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Other/Catering</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-blue-800 whitespace-nowrap bg-blue-50/30">Total Active</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-green-700 whitespace-nowrap bg-green-50/30">Balance</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-[#64748b] whitespace-nowrap">Ending</th>
              <th class="px-1.5 py-1 text-left text-[0.65rem] font-bold text-red-700 whitespace-nowrap bg-red-50/30">Deference</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in consumptionRows"
              :key="r.consumptionRecordId ?? `${r.ingredient}-${i}`"
              class="border-b hover:bg-slate-100/50 transition-colors"
              :class="r.type === 'product' ? 'bg-emerald-50/40' : ''"
            >
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-1.5 py-1 text-[0.65rem] font-bold whitespace-nowrap"
                :class="r.type === 'product' ? 'text-emerald-800' : 'text-[#0d4169]'"
              >
                {{ r.ingredient || '-' }}
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.bbf) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.storeRecv) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.nonStock) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.production) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-emerald-800 text-center bg-emerald-50/30">
                {{ (r.transInDetails || []).length ? `${(r.transInDetails || []).length} src` : '-' }}
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] text-emerald-700 font-bold text-right bg-emerald-50/20">{{ consumptionQty(r.transIn1) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right font-semibold text-red-700">
                <input type="checkbox" v-model="r.wastageSelected" class="h-3 w-3 mr-0.5 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ consumptionQty(r.wastage) }}</span>
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] font-black text-[#0d4169] text-right bg-slate-50/40">{{ consumptionQty(r.totalAvail) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.salesManual) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.salesSystem) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.mgmt) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.room) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.promo) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.coCost) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.spoilage) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right">{{ consumptionQty(r.other) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-blue-800 text-right font-semibold bg-blue-50/30">
                <input type="checkbox" v-model="r.transferIssueDSelected" class="h-3 w-3 mr-0.5 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ consumptionQty(r.transOut1) }}</span>
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] text-blue-700 font-bold text-right bg-blue-50/20">{{ consumptionQty(r.transOut2) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right font-semibold text-red-700">
                <input type="checkbox" v-model="r.spoiledItemSelected" class="h-3 w-3 mr-0.5 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ consumptionQty(r.spoiledItem) }}</span>
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-700 text-right font-semibold text-red-700">
                <input type="checkbox" v-model="r.cateringSelected" class="h-3 w-3 mr-0.5 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ consumptionQty(r.catering) }}</span>
              </td>
              <td class="px-1.5 py-1 text-[0.65rem] font-black text-blue-700 text-right bg-blue-50/30">{{ consumptionQty(r.totalUsed) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] font-black text-green-700 text-right bg-green-50/30">{{ consumptionQty(r.balance) }}</td>
              <td class="px-1.5 py-1 text-[0.65rem] text-slate-600 text-right">{{ consumptionQty(r.ending) }}</td>
              <td
                class="px-1.5 py-1 text-[0.65rem] font-black text-right bg-red-50/30"
                :class="Number(r.variance) < 0 ? 'text-red-500' : (Number(r.variance) > 0 ? 'text-green-500' : 'text-slate-700')"
              >
                {{ consumptionQty(r.variance) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingConsumption && consumptionRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowConsumptionEditAccess"
          :disabled="isSubmittingConsumptionEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingConsumptionEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingConsumptionEditAccess ? 'Saving...' : 'Allow Consumption Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingConsumption && consumptionShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No consumption has been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingConsumption" class="text-sm text-slate-500">
        Select Department and Calendar to load Consumption.
      </div>
    </div>

    <!-- Production: Material Inventory (saved_material_inventory only) -->
    <div v-if="selectedProduction === 'Material Inventory'" class="mt-10">
      <div v-if="materialInventoryError" class="text-sm text-red-600 font-semibold mb-3">
        {{ materialInventoryError }}
      </div>

      <div v-if="isLoadingMaterialInventory" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="materialInventoryRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table min-w-max">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item Code</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">{{ materialLastCountHeaderLabel }}</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Received</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Current Balance</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">New Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Difference</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in materialInventoryRows" :key="r.itemId ?? `${r.itemName}-${i}`" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.itemCode || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.categoryName || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] font-bold text-[#0d4169]">{{ r.itemName || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uomStock || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.lastCount }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.received }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.currentBalance }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">
                <input type="checkbox" v-model="r.newQtySelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.newQty }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.difference }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingMaterialInventory && materialInventoryRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowMaterialInventoryEditAccess"
          :disabled="isSubmittingMaterialInventoryEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingMaterialInventoryEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingMaterialInventoryEditAccess ? 'Saving...' : 'Allow Material Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingMaterialInventory && materialInventoryShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No material inventory has been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingMaterialInventory" class="text-sm text-slate-500">
        Select Department and Calendar to load Material Inventory.
      </div>
    </div>

    <!-- Production: Debone Inventory -->
    <div v-if="selectedProduction === 'Debone Inventory'" class="mt-10">
      <div v-if="deboneInventoryError" class="text-sm text-red-600 font-semibold mb-3">
        {{ deboneInventoryError }}
      </div>

      <div v-if="isLoadingDeboneInventory" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="deboneInventoryRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Ingredient Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">U.O.M</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty PC</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty Kg/L</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">T.Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in deboneInventoryRows"
              :key="r.deboneInventoryId ?? `${r.ingredientName}-${i}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-2 py-1.5 text-[0.7rem] font-bold"
                :class="r.type === 'debone' ? 'text-emerald-700' : 'text-[#0d4169]'"
              >
                {{ r.ingredientName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ r.uom || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtyPcSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtyPc }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.qtyKgLSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.qtyKgL }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ money(r.unitPrice) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-black">{{ money(r.tPrice) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.remarkSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.remark || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingDeboneInventory && deboneInventoryRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowDeboneInventoryEditAccess"
          :disabled="isSubmittingDeboneInventoryEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingDeboneInventoryEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingDeboneInventoryEditAccess ? 'Saving...' : 'Allow Debone Inventory Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingDeboneInventory && deboneInventoryShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No debone inventory has been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingDeboneInventory" class="text-sm text-slate-500">
        Select Calendar to load Debone Inventory{{ isDeboneProductionSelected ? ' (Department optional)' : '' }}.
      </div>
    </div>

    <!-- Production: Debone Controls -->
    <div v-if="selectedProduction === 'Debone Controls'" class="mt-10">
      <div v-if="deboneControlsError" class="text-sm text-red-600 font-semibold mb-3">
        {{ deboneControlsError }}
      </div>

      <div v-if="isLoadingDeboneControls" class="flex justify-center py-10">
        <span class="h-5 w-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></span>
      </div>

      <div v-else-if="deboneControlsRows.length > 0" class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <table class="w-full border-collapse bg-white overflow-hidden print-table min-w-max">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">No.</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Ingredient Name</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Wastage PC</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Wastage Kg/L</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Send To (D)</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Spoiled PC</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Spoiled Kg/L</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Catering PC</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Catering Kg/L</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in deboneControlsRows"
              :key="r.deboneControlId ?? `${r.ingredientName}-${i}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ i + 1 }}</td>
              <td
                class="px-2 py-1.5 text-[0.7rem] font-bold whitespace-nowrap"
                :class="r.type === 'debone' ? 'text-emerald-700' : 'text-[#0d4169]'"
              >
                {{ r.ingredientName || '-' }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.wastagePcSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.wastagePc }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.wastageKgLSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.wastageKgL }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.sendToDSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">PC {{ r.totalDistributedPc }} · Kg/L {{ r.totalDistributedKgL }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.spoiledItemPcSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.spoiledItemPc }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.spoiledItemKgLSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.spoiledItemKgL }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.cateringPcSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.cateringPc }}</span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">
                <input type="checkbox" v-model="r.cateringKgLSelected" class="h-3 w-3 mr-1 align-middle accent-[#0d4169]" />
                <span class="align-middle">{{ r.cateringKgL }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoadingDeboneControls && deboneControlsRows.length > 0" class="mt-4 flex justify-end">
        <button
          type="button"
          @click="handleAllowDeboneControlsEditAccess"
          :disabled="isSubmittingDeboneControlsEditAccess"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 inline-flex items-center gap-2"
        >
          <span
            v-if="isSubmittingDeboneControlsEditAccess"
            class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          ></span>
          <span>{{ isSubmittingDeboneControlsEditAccess ? 'Saving...' : 'Allow Debone Controls Edit Access' }}</span>
        </button>
      </div>

      <div
        v-else-if="!isLoadingDeboneControls && deboneControlsShowNoRecordsHint"
        class="text-sm text-slate-500"
      >
        No debone controls have been saved for this department and date.
      </div>
      <div v-else-if="!isLoadingDeboneControls" class="text-sm text-slate-500">
        Select Calendar to load Debone Controls{{ isDeboneProductionSelected ? ' (Department optional)' : '' }}.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const grvNumber = ref('')
const sivNumber = ref('')
const nsNumber = ref('')
const selectedProduction = ref('')
const selectedDepartment = ref('')
const calendarDate = ref('')
const departments = ref([])
const NO_DEPARTMENT = 'none'

const isDeboneProductionSelected = computed(() =>
  selectedProduction.value === 'Debone Inventory' || selectedProduction.value === 'Debone Controls'
)

const isCentralKitchenProductionSelected = computed(() =>
  selectedProduction.value === 'Central Inventory' ||
  selectedProduction.value === 'Central Product Distribution' ||
  selectedProduction.value === 'Central Item Controls'
)

function editAccessDepartmentQueryValue() {
  if (isDeboneProductionSelected.value) {
    if (!selectedDepartment.value || selectedDepartment.value === NO_DEPARTMENT) return NO_DEPARTMENT
    return String(selectedDepartment.value)
  }
  return selectedDepartment.value
}

function editAccessDepartmentIdForPayload() {
  const raw = editAccessDepartmentQueryValue()
  if (raw === NO_DEPARTMENT) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const currentUser = computed(() => store.getters.currentUser)
const userPrivileges = computed(() => {
  const p = currentUser.value?.privilege
  if (!p) return {}
  return typeof p === 'string' ? JSON.parse(p) : p
})

const filteredDepartments = computed(() => {
  // Requirement: Department dropdown must show all departments (no token-based filtering).
  return departments.value
})

const hasAllPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const hasAnyAction = (obj) => !!(obj && (obj.Add || obj.Edit || obj.View || obj.Void || obj.Approve))
const getPrivBlock = (name) =>
  userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null

const canSeeGrvField = computed(() => hasAllPrivilege.value || hasAnyAction(getPrivBlock('GRV Edit Access')))
const canSeeSivField = computed(() => hasAllPrivilege.value || hasAnyAction(getPrivBlock('SIV Edit Access')))
const canSeeNsField = computed(() => hasAllPrivilege.value || hasAnyAction(getPrivBlock('Non- Stockable Edit Access')))
const canSeeAnyField = computed(() => canSeeGrvField.value || canSeeSivField.value || canSeeNsField.value)

const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const isFinding = ref(false)
const isSubmittingEditAccess = ref(false)
const hasSearched = ref(false)
const grvRows = ref([])
const sivRows = ref([])
const nsRows = ref([])

const isLoadingProduction = ref(false)
const productionError = ref('')
const dailyInventoryRows = ref([])
const dailyInventoryShowNoRecordsHint = ref(false)
const isSubmittingDailyInvEditAccess = ref(false)

const isLoadingCentralInventory = ref(false)
const centralInventoryError = ref('')
const centralInventoryRows = ref([])
const centralInventoryShowNoRecordsHint = ref(false)
const isSubmittingCentralInvEditAccess = ref(false)

const isLoadingProductDistribution = ref(false)
const productDistributionError = ref('')
const productDistributionRows = ref([])
const productDistributionShowNoRecordsHint = ref(false)
const isSubmittingProductDistributionEditAccess = ref(false)

const isLoadingCentralItemControl = ref(false)
const centralItemControlError = ref('')
const centralItemControlRows = ref([])
const centralItemControlShowNoRecordsHint = ref(false)
const isSubmittingCentralItemControlEditAccess = ref(false)

const isLoadingDailyProducts = ref(false)
const dailyProductsError = ref('')
const dailyProductsRows = ref([])
const dailyProductsShowNoRecordsHint = ref(false)
const isSubmittingDailyProdEditAccess = ref(false)

const isLoadingDailyTransaction = ref(false)
const dailyTransactionError = ref('')
const dailyTransactionRows = ref([])
const dailyTransactionShowNoRecordsHint = ref(false)
const isSubmittingDailyTransEditAccess = ref(false)

const isLoadingConsumption = ref(false)
const consumptionError = ref('')
const consumptionRows = ref([])
const consumptionShowNoRecordsHint = ref(false)
const isSubmittingConsumptionEditAccess = ref(false)

const isLoadingMaterialInventory = ref(false)
const materialInventoryError = ref('')
const materialInventoryRows = ref([])
const materialInventoryShowNoRecordsHint = ref(false)
const isSubmittingMaterialInventoryEditAccess = ref(false)
const isLoadingDeboneInventory = ref(false)
const deboneInventoryError = ref('')
const deboneInventoryRows = ref([])
const deboneInventoryShowNoRecordsHint = ref(false)
const isSubmittingDeboneInventoryEditAccess = ref(false)
const isLoadingDeboneControls = ref(false)
const deboneControlsError = ref('')
const deboneControlsRows = ref([])
const deboneControlsShowNoRecordsHint = ref(false)
const isSubmittingDeboneControlsEditAccess = ref(false)
const materialLastCountHeaderLabel = computed(() => {
  const firstDate = materialInventoryRows.value?.[0]?.lastCountDate
  return firstDate ? `Last Count / ${toYmd(firstDate)}` : 'Last Count / Date'
})

const itemCategoryByItemId = ref(new Map())
const categoryNameById = ref(new Map())
const departmentNameById = ref(new Map())

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
  }
}, { deep: true })

watch(selectedProduction, (prod) => {
  productionError.value = ''
  centralInventoryError.value = ''
  productDistributionError.value = ''
  centralItemControlError.value = ''
  if (prod === 'Debone Inventory' || prod === 'Debone Controls') {
    if (!selectedDepartment.value) selectedDepartment.value = NO_DEPARTMENT
  } else if (isCentralKitchenProductionSelected.value) {
    if (selectedDepartment.value === NO_DEPARTMENT) selectedDepartment.value = ''
  } else if (selectedDepartment.value === NO_DEPARTMENT) {
    selectedDepartment.value = ''
  }
  if (prod !== 'Daily Inventory') {
    dailyInventoryRows.value = []
    dailyInventoryShowNoRecordsHint.value = false
  }
  if (prod !== 'Central Inventory') {
    centralInventoryRows.value = []
    centralInventoryShowNoRecordsHint.value = false
  }
  if (prod !== 'Central Product Distribution') {
    productDistributionRows.value = []
    productDistributionShowNoRecordsHint.value = false
  }
  if (prod !== 'Central Item Controls') {
    centralItemControlRows.value = []
    centralItemControlShowNoRecordsHint.value = false
  }
  dailyProductsError.value = ''
  if (prod !== 'Daily products') {
    dailyProductsRows.value = []
    dailyProductsShowNoRecordsHint.value = false
  }
  dailyTransactionError.value = ''
  if (prod !== 'Daily Transaction') {
    dailyTransactionRows.value = []
    dailyTransactionShowNoRecordsHint.value = false
  }
  consumptionError.value = ''
  if (prod !== 'Consumption') {
    consumptionRows.value = []
    consumptionShowNoRecordsHint.value = false
  }
  materialInventoryError.value = ''
  if (prod !== 'Material Inventory') {
    materialInventoryRows.value = []
    materialInventoryShowNoRecordsHint.value = false
  }
  deboneInventoryError.value = ''
  if (prod !== 'Debone Inventory') {
    deboneInventoryRows.value = []
    deboneInventoryShowNoRecordsHint.value = false
  }
  deboneControlsError.value = ''
  if (prod !== 'Debone Controls') {
    deboneControlsRows.value = []
    deboneControlsShowNoRecordsHint.value = false
  }
})

watch([selectedDepartment, calendarDate], () => {
  dailyInventoryShowNoRecordsHint.value = false
  centralInventoryShowNoRecordsHint.value = false
  productDistributionShowNoRecordsHint.value = false
  centralItemControlShowNoRecordsHint.value = false
  dailyProductsShowNoRecordsHint.value = false
  dailyTransactionShowNoRecordsHint.value = false
  consumptionShowNoRecordsHint.value = false
  materialInventoryShowNoRecordsHint.value = false
  deboneInventoryShowNoRecordsHint.value = false
  deboneControlsShowNoRecordsHint.value = false
})

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const raw = Array.isArray(data) ? data : []
    // Keep only active departments; userDeptList filter happens in filteredDepartments
    departments.value = raw.filter(d => (d.status ?? 1) === 1)
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

onMounted(() => {
  fetchDepartments()
})

async function loadDailyInventory() {
  const deptId = selectedDepartment.value
  const cal = calendarDate.value
  if (!deptId || !cal) {
    productionError.value = 'Please select Department and Calendar.'
    dailyInventoryRows.value = []
    dailyInventoryShowNoRecordsHint.value = false
    return
  }

  isLoadingProduction.value = true
  productionError.value = ''
  dailyInventoryShowNoRecordsHint.value = false
  try {
    // Same endpoint used by dailyInventoryPage.vue for saved snapshots.
    const res = await apiFetch(`/daily-inventory?departmentId=${deptId}&calendar=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load daily inventory')
    }
    // Same shape as dailyInventoryPage.vue: { rows } from GET /daily-inventory are daily_inventories rows only.
    // pendingEditAccess on the payload is for the posting page, not used here.
    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    let savedRows = []
    if (Array.isArray(payload)) savedRows = payload
    else if (payload?.rows) savedRows = payload.rows
    else if (payload?.data) savedRows = payload.data

    dailyInventoryRows.value = (Array.isArray(savedRows) ? savedRows : [])
      .map((row) => ({
        ...row,
        // PK on daily_inventories — used for edit-access POST
        dailyInventoryId: row.id != null ? Number(row.id) : null,
        id: row.id ?? (row.type === 'item' ? row.itemId : row.productId),
        departmentId: row.departmentId ?? row.department_id ?? selectedDepartment.value ?? null,
        ingredientName: row.ingredientName || row.ingredient_name || row.name || '-',
        // Normalized like dailyInventoryPage saved snapshot (all from daily_inventories row)
        converted: Number(row?.converted ?? 0),
        totalQty: Number(row?.totalQty ?? row?.total_qty ?? 0),
        unitPrice: Number(row?.unitPrice ?? row?.unit_price ?? 0),
        tPrice: Number(row?.totalPrice ?? row?.tPrice ?? row?.total_price ?? 0),
        qtyPack: Number(row.qtyPack ?? row.quantityPack ?? row.qty_pack ?? 0),
        volume: Number(row?.volume ?? 0),
        qtySingle: Number(row.qtySingle ?? row.quantitySingle ?? row.qty_single ?? 0),
        uPricePack: Number(row.uPricePack ?? row.unitPricePack ?? row.u_price_pack ?? 0),
        uPriceSingle: Number(row.uPriceSingle ?? row.unitPriceSingle ?? row.u_price_single ?? 0),
        remark: row.remark ?? row.notes ?? '',
        itemId: row.itemId ?? row.item_id ?? null,
        productId: row.productId ?? row.product_id ?? null,
        qtyPackSelected: false,
        volumeSelected: false,
        qtySingleSelected: false,
        remarkSelected: false,
      }))
      .sort((a, b) => String(a.ingredientName || '').localeCompare(String(b.ingredientName || '')))
    dailyInventoryShowNoRecordsHint.value = dailyInventoryRows.value.length === 0
  } catch (err) {
    console.error(err)
    productionError.value = err?.message || 'Failed to load daily inventory'
    dailyInventoryRows.value = []
    dailyInventoryShowNoRecordsHint.value = false
  } finally {
    isLoadingProduction.value = false
  }
}

function money(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })
}

function consumptionQty(val) {
  const n = Number(String(val ?? 0).replace(/,/g, ''))
  const x = Number.isFinite(n) ? n : 0
  return x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

async function loadDailyProducts() {
  const deptId = selectedDepartment.value
  const cal = calendarDate.value
  if (!deptId || !cal) {
    dailyProductsError.value = 'Please select Department and Calendar.'
    dailyProductsRows.value = []
    dailyProductsShowNoRecordsHint.value = false
    return
  }

  isLoadingDailyProducts.value = true
  dailyProductsError.value = ''
  dailyProductsShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(`/production?departmentId=${deptId}&calendar=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load daily products')
    }
    const payload = await res.json().catch(() => [])
    let raw = []
    if (Array.isArray(payload)) raw = payload
    else if (payload?.rows) raw = payload.rows
    else if (payload?.data) raw = payload.data

    dailyProductsRows.value = (Array.isArray(raw) ? raw : []).map((row) => ({
      ...row,
      productionId: row.productionId ?? null,
      productId: row.productId ?? row.id ?? null,
      productName: row.productName || row.name || '-',
      uom: row.uom || '-',
      quantity: Number(row.quantity ?? 0),
      quantitySelected: false,
    }))
      .sort((a, b) => String(a.productName || '').localeCompare(String(b.productName || '')))
    dailyProductsShowNoRecordsHint.value = dailyProductsRows.value.length === 0
  } catch (err) {
    console.error(err)
    dailyProductsError.value = err?.message || 'Failed to load daily products'
    dailyProductsRows.value = []
    dailyProductsShowNoRecordsHint.value = false
  } finally {
    isLoadingDailyProducts.value = false
  }
}

async function loadCentralInventory() {
  const cal = calendarDate.value
  if (!cal) {
    centralInventoryError.value = 'Please select Calendar.'
    centralInventoryRows.value = []
    centralInventoryShowNoRecordsHint.value = false
    return
  }

  isLoadingCentralInventory.value = true
  centralInventoryError.value = ''
  centralInventoryShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(`/central-kitchen/central-inventory?calendar=${encodeURIComponent(cal)}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load central inventory')
    }
    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    let savedRows = []
    if (Array.isArray(payload)) savedRows = payload
    else if (payload?.rows) savedRows = payload.rows
    else if (payload?.data) savedRows = payload.data

    centralInventoryRows.value = (Array.isArray(savedRows) ? savedRows : [])
      .map((row) => ({
        ...row,
        centralInventoryId: row.id != null ? Number(row.id) : null,
        id: row.id ?? (row.type === 'item' ? row.itemId : row.centralProductId),
        ingredientName: row.ingredientName || row.ingredient_name || row.name || '-',
        converted: Number(row?.converted ?? 0),
        totalQty: Number(row?.totalQty ?? row?.total_qty ?? 0),
        unitPrice: Number(row?.unitPrice ?? row?.unit_price ?? 0),
        tPrice: Number(row?.totalPrice ?? row?.tPrice ?? row?.total_price ?? 0),
        qtyPack: Number(row.qtyPack ?? row.quantityPack ?? row.qty_pack ?? 0),
        volume: Number(row?.volume ?? 0),
        qtySingle: Number(row.qtySingle ?? row.quantitySingle ?? row.qty_single ?? 0),
        uomStore: row.uomStore ?? row.uom_store ?? '-',
        uomFB: row.uomFB ?? row.uom_fb ?? '-',
        remark: row.remark ?? row.notes ?? '',
        itemId: row.itemId ?? row.item_id ?? null,
        centralProductId: row.centralProductId ?? row.central_product_id ?? null,
        qtyPackSelected: false,
        volumeSelected: false,
        qtySingleSelected: false,
        remarkSelected: false,
      }))
      .sort((a, b) => String(a.ingredientName || '').localeCompare(String(b.ingredientName || '')))
    centralInventoryShowNoRecordsHint.value = centralInventoryRows.value.length === 0
  } catch (err) {
    console.error(err)
    centralInventoryError.value = err?.message || 'Failed to load central inventory'
    centralInventoryRows.value = []
    centralInventoryShowNoRecordsHint.value = false
  } finally {
    isLoadingCentralInventory.value = false
  }
}

function formatQty(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 5 })
}

function formatProductDistributionTransferD(row) {
  const details = row?.transferIssueD ?? row?.transOutDetails ?? []
  if (!Array.isArray(details) || details.length === 0) return '-'
  const withQty = details.filter((d) => Number(d?.quantity ?? 0) > 0)
  if (withQty.length === 0) return '-'
  return withQty
    .map((d) => `${d.displayLabel || d.name || 'Dept'}: ${formatQty(d.quantity)}`)
    .join(', ')
}

function formatCentralItemControlTransferD(row) {
  const details = row?.transOutDetails ?? []
  if (!Array.isArray(details) || details.length === 0) return '-'
  const withQty = details.filter((d) => Number(d?.quantity ?? 0) > 0)
  if (withQty.length === 0) return '-'
  return withQty
    .map((d) => `${d.displayLabel || d.name || 'Dept'}: ${formatQty(d.quantity)}`)
    .join(', ')
}

async function loadCentralItemControlForEditAccess() {
  const cal = calendarDate.value
  if (!cal) {
    centralItemControlError.value = 'Please select Calendar.'
    centralItemControlRows.value = []
    centralItemControlShowNoRecordsHint.value = false
    return
  }

  isLoadingCentralItemControl.value = true
  centralItemControlError.value = ''
  centralItemControlShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(`/central-kitchen/central-item-control?calendar=${encodeURIComponent(cal)}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load central item controls')
    }

    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    const savedRows = Array.isArray(payload?.rows) ? payload.rows : []

    if (!payload?.hasData || savedRows.length === 0) {
      centralItemControlRows.value = []
      centralItemControlShowNoRecordsHint.value = true
      return
    }

    centralItemControlRows.value = savedRows
      .map((row) => ({
        ...row,
        centralItemControlId: row.id != null ? Number(row.id) : (row.centralItemControlId != null ? Number(row.centralItemControlId) : null),
        type: row.type || 'item',
        itemId: row.itemId ?? row.item_id ?? null,
        centralProductId: row.centralProductId ?? row.central_product_id ?? null,
        ingredientName: row.ingredientName || row.ingredient || '-',
        wastage: Number(row.wastage ?? 0),
        spoiledItem: Number(row.spoiledItem ?? row.spoiled_item ?? 0),
        catering: Number(row.catering ?? 0),
        transOutDetails: Array.isArray(row.transOutDetails) ? row.transOutDetails : [],
        wastageSelected: false,
        transferIssueDSelected: false,
        spoiledItemSelected: false,
        cateringSelected: false,
      }))
      .sort((a, b) => String(a.ingredientName || '').localeCompare(String(b.ingredientName || '')))
    centralItemControlShowNoRecordsHint.value = centralItemControlRows.value.length === 0
  } catch (err) {
    console.error(err)
    centralItemControlError.value = err?.message || 'Failed to load central item controls'
    centralItemControlRows.value = []
    centralItemControlShowNoRecordsHint.value = false
  } finally {
    isLoadingCentralItemControl.value = false
  }
}

async function handleAllowCentralItemControlEditAccess() {
  if (isSubmittingCentralItemControlEditAccess.value) return
  if (!centralItemControlRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!calendarDate.value) {
    alert('Please select Calendar')
    return
  }

  const selectedRows = centralItemControlRows.value.filter((r) => !!(
    r.wastageSelected ||
    r.transferIssueDSelected ||
    r.spoiledItemSelected ||
    r.cateringSelected
  ))
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Wastage, Transfer Issu (D), Spoiled By Items, Other/Catering).')
    return
  }

  const missingId = selectedRows.filter(
    (r) => r.centralItemControlId == null || !Number.isFinite(Number(r.centralItemControlId))
  )
  if (missingId.length > 0) {
    alert('Some rows are missing the saved item control id. Reload Central Item Controls (Find) and try again.')
    return
  }

  const rows = selectedRows.map((r) => ({
    centralItemControlId: Number(r.centralItemControlId),
    calendar: calendarDate.value,
    type: r.type || null,
    itemId: r.type === 'item' ? (r.itemId ?? null) : null,
    centralProductId: r.type === 'product' ? (r.centralProductId ?? null) : null,
    wastage: !!r.wastageSelected,
    transfer_issue_d: !!r.transferIssueDSelected,
    spoiled_item: !!r.spoiledItemSelected,
    catering: !!r.cateringSelected,
  }))

  try {
    isSubmittingCentralItemControlEditAccess.value = true
    const res = await apiFetch('/edit-access/central-item-control', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request central item controls edit access')
    }
    alert('Central item controls edit access requests submitted successfully')
    centralItemControlRows.value = []
    centralItemControlShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit central item controls edit access request')
  } finally {
    isSubmittingCentralItemControlEditAccess.value = false
  }
}

async function loadProductDistributionForEditAccess() {
  const cal = calendarDate.value
  if (!cal) {
    productDistributionError.value = 'Please select Calendar.'
    productDistributionRows.value = []
    productDistributionShowNoRecordsHint.value = false
    return
  }

  isLoadingProductDistribution.value = true
  productDistributionError.value = ''
  productDistributionShowNoRecordsHint.value = false
  try {
    const [res, productsRes] = await Promise.all([
      apiFetch(`/central-kitchen/product-distribution?calendar=${encodeURIComponent(cal)}`),
      apiFetch('/central-kitchen/central-products?activeOnly=true'),
    ])
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load central product distribution')
    }

    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    let savedRows = []
    if (Array.isArray(payload)) savedRows = payload
    else if (payload?.rows) savedRows = payload.rows
    else if (payload?.data) savedRows = payload.data

    const productsPayload = productsRes.ok ? await productsRes.json().catch(() => []) : []
    const products = Array.isArray(productsPayload) ? productsPayload : []
    const productNameById = new Map(
      products.map((p) => [String(p.id), String(p.name || p.productName || '').trim() || 'Central Product'])
    )

    productDistributionRows.value = (Array.isArray(savedRows) ? savedRows : [])
      .map((row) => {
        const centralProductId = row.centralProductId ?? row.central_product_id ?? row.productId ?? null
        const transferIssueD = Array.isArray(row.transferIssueD)
          ? row.transferIssueD
          : (Array.isArray(row.transOutDetails) ? row.transOutDetails : [])
        return {
          ...row,
          productDistributionId: row.id != null ? Number(row.id) : null,
          centralProductId,
          productName: productNameById.get(String(centralProductId)) || row.productName || row.name || '-',
          totalProduced: Number(row.totalProduced ?? row.total_produced ?? 0),
          transferIssueD: transferIssueD.map((d) => ({
            id: d.departmentId ?? d.id,
            name: d.name || '',
            displayLabel: d.displayLabel || d.display_label || '',
            quantity: Number(d.quantity ?? 0),
          })),
          transOutTotal: Number(row.transOutTotal ?? row.transferIssueT ?? row.transfer_issue_t ?? 0),
          totalProducedSelected: false,
          transferIssueDSelected: false,
        }
      })
      .sort((a, b) => String(a.productName || '').localeCompare(String(b.productName || '')))
    productDistributionShowNoRecordsHint.value = productDistributionRows.value.length === 0
  } catch (err) {
    console.error(err)
    productDistributionError.value = err?.message || 'Failed to load central product distribution'
    productDistributionRows.value = []
    productDistributionShowNoRecordsHint.value = false
  } finally {
    isLoadingProductDistribution.value = false
  }
}

async function handleAllowProductDistributionEditAccess() {
  if (isSubmittingProductDistributionEditAccess.value) return
  if (!productDistributionRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!calendarDate.value) {
    alert('Please select Calendar')
    return
  }

  const selectedRows = productDistributionRows.value.filter(
    (r) => !!(r.totalProducedSelected || r.transferIssueDSelected)
  )
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Total Produced, Transfer Issu (D)).')
    return
  }

  const missingId = selectedRows.filter(
    (r) => r.productDistributionId == null || !Number.isFinite(Number(r.productDistributionId))
  )
  if (missingId.length > 0) {
    alert('Some rows are missing the saved distribution id. Reload Central Product Distribution (Find) and try again.')
    return
  }

  const rows = selectedRows.map((r) => ({
    productDistributionId: Number(r.productDistributionId),
    calendar: calendarDate.value,
    centralProductId: r.centralProductId ?? null,
    total_produced: !!r.totalProducedSelected,
    transfer_issue_d: !!r.transferIssueDSelected,
  }))

  try {
    isSubmittingProductDistributionEditAccess.value = true
    const res = await apiFetch('/edit-access/product-distribution', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request central product distribution edit access')
    }
    alert('Central product distribution edit access requests submitted successfully')
    productDistributionRows.value = []
    productDistributionShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit central product distribution edit access request')
  } finally {
    isSubmittingProductDistributionEditAccess.value = false
  }
}

async function handleAllowCentralInventoryEditAccess() {
  if (isSubmittingCentralInvEditAccess.value) return
  if (!centralInventoryRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!calendarDate.value) {
    alert('Please select Calendar')
    return
  }

  const selectedRows = centralInventoryRows.value.filter(
    (r) => !!(r.qtyPackSelected || r.volumeSelected || r.qtySingleSelected || r.remarkSelected)
  )
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Qty Pack, Volume, Qty Single, Remark).')
    return
  }

  const missingInvId = selectedRows.filter(
    (r) => r.centralInventoryId == null || !Number.isFinite(Number(r.centralInventoryId))
  )
  if (missingInvId.length > 0) {
    alert('Some rows are missing the saved inventory id. Reload Central Inventory (Find) and try again.')
    return
  }

  const rows = selectedRows.map((r) => ({
    centralInventoryId: Number(r.centralInventoryId),
    calendar: calendarDate.value,
    itemId: r.type === 'item' ? (r.itemId ?? null) : (r.itemId ?? null),
    centralProductId: r.type === 'product' ? (r.centralProductId ?? null) : (r.centralProductId ?? null),
    itemType: r.type || null,
    qty_pack: !!r.qtyPackSelected,
    volume: !!r.volumeSelected,
    qty_single: !!r.qtySingleSelected,
    remark: !!r.remarkSelected,
  }))

  try {
    isSubmittingCentralInvEditAccess.value = true
    const res = await apiFetch('/edit-access/central-inventory', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request central inventory edit access')
    }
    alert('Central inventory edit access requests submitted successfully')
    centralInventoryRows.value = []
    centralInventoryShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit central inventory edit access request')
  } finally {
    isSubmittingCentralInvEditAccess.value = false
  }
}

async function handleAllowDailyInventoryEditAccess() {
  if (isSubmittingDailyInvEditAccess.value) return
  if (!dailyInventoryRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!selectedDepartment.value || !calendarDate.value) {
    alert('Please select Department and Calendar')
    return
  }

  const selectedRows = dailyInventoryRows.value.filter(r => !!(r.qtyPackSelected || r.volumeSelected || r.qtySingleSelected || r.remarkSelected))
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Qty Pack, Volume, Qty Single, Remark).')
    return
  }

  const missingInvId = selectedRows.filter((r) => r.dailyInventoryId == null || !Number.isFinite(Number(r.dailyInventoryId)))
  if (missingInvId.length > 0) {
    alert('Some rows are missing the saved inventory id. Reload Daily Inventory (Find) and try again.')
    return
  }

  // dailyInventoryId must be daily_inventories.id — do not fall back to r.id (that can be itemId/productId when id was absent).
  const rows = selectedRows.map((r) => ({
    dailyInventoryId: Number(r.dailyInventoryId),
    departmentId: r.departmentId ?? selectedDepartment.value,
    calendar: calendarDate.value,
    itemId: r.type === 'item' ? (r.itemId ?? null) : (r.itemId ?? null),
    productId: r.type === 'product' ? (r.productId ?? null) : (r.productId ?? null),
    itemType: r.type || null,
    qty_pack: !!r.qtyPackSelected,
    volume: !!r.volumeSelected,
    qty_single: !!r.qtySingleSelected,
    remark: !!r.remarkSelected,
  }))

  try {
    isSubmittingDailyInvEditAccess.value = true
    const res = await apiFetch('/edit-access/daily-inventory', {
      method: 'POST',
      body: JSON.stringify({ rows })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request daily inventory edit access')
    }
    alert('Daily inventory edit access requests submitted successfully')
    // Clear the table after successful submission
    dailyInventoryRows.value = []
    dailyInventoryShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit daily inventory edit access request')
  } finally {
    isSubmittingDailyInvEditAccess.value = false
  }
}

function toYmd(d) {
  if (!d) return '-'
  try {
    return new Date(d).toISOString().split('T')[0]
  } catch {
    return String(d)
  }
}

function pickItemName(item) {
  return (
    item?.description ||
    item?.item?.itemName ||
    item?.item?.name ||
    item?.product?.name ||
    item?.productName ||
    item?.itemName ||
    '-'
  )
}

function pickCategoryName(item) {
  return (
    item?.category?.name ||
    item?.item?.category?.name ||
    item?.categoryName ||
    '-'
  )
}

function splitCsv(val) {
  if (val == null) return []
  const s = String(val).trim()
  if (!s) return []
  if (!s.includes(',')) return [s]
  return s.split(',').map(x => x.trim()).filter(Boolean)
}

function isLiterOrKgUnit(unit) {
  const u = String(unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  return (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'k.g' ||
    u === 'k.g.'
  )
}

function pickDepartmentName(siv) {
  const depId = Number(siv?.departmentId ?? siv?.department_id ?? NaN)
  return (
    siv?.department?.name ||
    siv?.departmentName ||
    siv?.department?.departmentName ||
    (Number.isFinite(depId) ? (departmentNameById.value.get(depId) || '-') : '-') ||
    '-'
  )
}

function pickSivRowDepartmentName(siv) {
  // Requirement: Department must come from SIV's departmentId (header), not from item/product.
  return pickDepartmentName(siv)
}

async function ensureItemAndCategoryLookups() {
  // Build itemId -> categoryId and categoryId -> categoryName maps (used only when report payload lacks category info).
  if (itemCategoryByItemId.value.size > 0 && categoryNameById.value.size > 0) return

  const queryParts = []
  if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
  else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
  const q = queryParts.length ? `?${queryParts.join('&')}` : ''

  const [itemsRes, catsRes] = await Promise.all([
    apiFetch(`/items${q}`),
    apiFetch(`/categories${q}`),
  ])

  if (catsRes.ok) {
    const cats = await catsRes.json()
    for (const c of (Array.isArray(cats) ? cats : (cats?.data || []))) {
      const id = Number(c?.id)
      if (!Number.isFinite(id)) continue
      categoryNameById.value.set(id, c?.name || c?.categoryName || '-')
    }
  }

  if (itemsRes.ok) {
    const items = await itemsRes.json()
    for (const it of (Array.isArray(items) ? items : (items?.data || []))) {
      const id = Number(it?.id)
      const catId = Number(it?.categoryId ?? it?.category_id ?? it?.category?.id ?? NaN)
      if (!Number.isFinite(id) || !Number.isFinite(catId)) continue
      itemCategoryByItemId.value.set(id, catId)
    }
  }
}

async function ensureDepartmentLookups() {
  if (departmentNameById.value.size > 0) return

  const queryParts = []
  if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
  else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
  const q = queryParts.length ? `?${queryParts.join('&')}` : ''

  const res = await apiFetch(`/departments${q}`)
  if (!res.ok) return
  const deps = await res.json().catch(() => null)
  for (const d of (Array.isArray(deps) ? deps : (deps?.data || []))) {
    const id = Number(d?.id)
    if (!Number.isFinite(id)) continue
    departmentNameById.value.set(id, d?.name || d?.departmentName || '-')
  }
}

function getCategoryNameFromItemId(itemId) {
  const id = Number(itemId)
  if (!Number.isFinite(id)) return '-'
  const catId = itemCategoryByItemId.value.get(id)
  if (!catId) return '-'
  return categoryNameById.value.get(Number(catId)) || '-'
}

function expandGrvItemToRows(header, it) {
  // Match grvPage.vue behavior: split comma-separated details into multiple rows.
  const qtyArr = splitCsv(it?.quantityDetails ?? it?.quantity)
  const priceArr = splitCsv(it?.unitPriceDetails ?? it?.unitPrice)
  const volArr = splitCsv(it?.variantDetails ?? it?.variant ?? it?.remark)
  const expArr = splitCsv(it?.expiryDateDetails ?? it?.expiryDate ?? it?.expiry ?? it?.expiry_date)

  const maxLen = Math.max(1, qtyArr.length, priceArr.length, volArr.length, expArr.length)
  const rows = []
  for (let i = 0; i < maxLen; i++) {
    const qRaw = qtyArr[i] ?? qtyArr[0] ?? '0'
    const pRaw = priceArr[i] ?? priceArr[0] ?? '0'
    const vRaw = volArr[i] ?? volArr[0] ?? '-'
    const eRaw = expArr[i] ?? expArr[0] ?? '-'
    const q = Number(qRaw)
    const p = Number(pRaw)
    const rowTotal = (Number.isFinite(q) && Number.isFinite(p)) ? (q * p) : 0
    rows.push({
      ...header,
      // category/item/other meta filled outside
      qty: Number.isFinite(q) ? q : 0,
      volume: vRaw,
      unitPrice: Number.isFinite(p) ? p : 0,
      // Important: for comma-separated details, total must be per-row (qty * unitPrice),
      // not the overall item's total repeated on every split row.
      totalPrice: rowTotal,
      expiry: eRaw === '-' ? '-' : toYmd(eRaw),
      taxable: !!(it?.isTaxable ?? it?.taxable),
    })
  }
  return rows
}

function expandSivItemToRows(header, it) {
  const qtyArr = splitCsv(it?.quantityDetails ?? it?.quantity)
  const volArr = splitCsv(it?.variantDetails ?? it?.variant)
  const maxLen = Math.max(1, qtyArr.length, volArr.length)
  const unit = it?.product?.uom ?? it?.item?.unit ?? it?.unit ?? it?.uom ?? ''
  const needsConversion = isLiterOrKgUnit(unit)
  const rows = []
  for (let i = 0; i < maxLen; i++) {
    // IMPORTANT: each split row must use its own index (no fallback to index 0),
    // otherwise quantities repeat and become incorrect.
    const qRaw = qtyArr[i] ?? '0'
    const vRaw = volArr[i] ?? '-'
    const q = Number(qRaw)
    const v = Number(vRaw)
    const qtyComputed = (needsConversion && Number.isFinite(q) && Number.isFinite(v))
      ? ((q * v) / 1000)
      : q
    rows.push({
      ...header,
      qty: Number.isFinite(qtyComputed) ? qtyComputed : 0,
      volume: vRaw,
      remark: it?.remark ?? header?.remark ?? '-',
    })
  }
  return rows
}

function expandNsItemToRows(header, it) {
  const qtyArr = splitCsv(it?.quantityDetails ?? it?.quantity)
  const volArr = splitCsv(it?.variantDetails ?? it?.variant ?? it?.remark)
  const maxLen = Math.max(1, qtyArr.length, volArr.length)
  const rows = []
  for (let i = 0; i < maxLen; i++) {
    const qRaw = qtyArr[i] ?? qtyArr[0] ?? '0'
    const vRaw = volArr[i] ?? volArr[0] ?? '-'
    const q = Number(qRaw)
    rows.push({
      ...header,
      qty: Number.isFinite(q) ? q : 0,
      volume: vRaw,
      taxable: !!(it?.isTaxable ?? it?.taxable),
    })
  }
  return rows
}

async function loadDailyTransaction() {
  const deptId = selectedDepartment.value
  const cal = calendarDate.value
  if (!deptId || !cal) {
    dailyTransactionError.value = 'Please select Department and Calendar.'
    dailyTransactionRows.value = []
    dailyTransactionShowNoRecordsHint.value = false
    return
  }

  isLoadingDailyTransaction.value = true
  dailyTransactionError.value = ''
  dailyTransactionShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(
      `/daily-transactions?departmentId=${deptId}&calendar=${cal}&requireSavedSnapshot=1`
    )
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load daily transaction')
    }
    const payload = await res.json().catch(() => null)

    // API returns draft menu rows when hasData is false; Edit Access only lists saved daily_transaction rows.
    if (payload && typeof payload === 'object' && !Array.isArray(payload) && payload.hasData === false) {
      dailyTransactionRows.value = []
      dailyTransactionShowNoRecordsHint.value = true
      return
    }

    const rows = Array.isArray(payload) ? payload : (payload?.rows || payload?.data || [])

    dailyTransactionRows.value = (Array.isArray(rows) ? rows : []).map((r) => ({
      ...r,
      dailyTransactionId: r.dailyTransactionId ?? r.daily_transaction_id ?? null,
      menuId: r.menuId ?? r.menu_id ?? null,
      item: r.item ?? r.menuItem ?? r.menu?.name ?? r.name ?? '-',
      salesManual: Number(r.salesManual ?? r.sales_manual ?? 0),
      salesSystem: Number(r.salesSystem ?? r.sales_system ?? 0),
      management: Number(r.management ?? r.mgmt ?? 0),
      room: Number(r.room ?? 0),
      promotion: Number(r.promotion ?? r.promo ?? 0),
      companyCost: Number(r.companyCost ?? r.company_cost ?? 0),
      spoilage: Number(r.spoilage ?? 0),
      other: Number(r.other ?? 0),
      total: Number(r.total ?? 0),
      salesManualSelected: false,
      managementSelected: false,
      roomSelected: false,
      promotionSelected: false,
      companyCostSelected: false,
      spoilageSelected: false,
      otherSelected: false,
    }))
      .sort((a, b) => String(a.item || '').localeCompare(String(b.item || '')))
    dailyTransactionShowNoRecordsHint.value = dailyTransactionRows.value.length === 0
  } catch (err) {
    console.error(err)
    dailyTransactionError.value = err?.message || 'Failed to load daily transaction'
    dailyTransactionRows.value = []
    dailyTransactionShowNoRecordsHint.value = false
  } finally {
    isLoadingDailyTransaction.value = false
  }
}

async function loadDeboneInventoryForEditAccess() {
  const cal = calendarDate.value
  if (!cal) {
    deboneInventoryError.value = 'Please select Calendar.'
    deboneInventoryRows.value = []
    deboneInventoryShowNoRecordsHint.value = false
    return
  }

  isLoadingDeboneInventory.value = true
  deboneInventoryError.value = ''
  deboneInventoryShowNoRecordsHint.value = false
  try {
    const deptParam = encodeURIComponent(editAccessDepartmentQueryValue())
    const res = await apiFetch(`/debones/inventory?departmentId=${deptParam}&calendar=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load debone inventory')
    }
    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    const savedRows = Array.isArray(payload?.rows) ? payload.rows : []

    if (!payload?.hasData || savedRows.length === 0) {
      deboneInventoryRows.value = []
      deboneInventoryShowNoRecordsHint.value = true
      return
    }

    deboneInventoryRows.value = savedRows
      .map((row) => ({
        ...row,
        deboneInventoryId: row.id != null ? Number(row.id) : null,
        type: row.type || row.kind || 'item',
        ingredientName: row.ingredientName || '-',
        uom: row.uom || '-',
        qtyPc: Number(row.qtyPc ?? 0),
        qtyKgL: Number(row.qtyKgL ?? 0),
        unitPrice: Number(row.unitPrice ?? 0),
        tPrice: Number(row.tPrice ?? row.totalPrice ?? 0),
        remark: row.remark ?? '',
        itemId: row.itemId ?? null,
        deboneRegistrationId: row.deboneRegistrationId ?? null,
        qtyPcSelected: false,
        qtyKgLSelected: false,
        remarkSelected: false,
      }))
      .sort((a, b) => String(a.ingredientName || '').localeCompare(String(b.ingredientName || '')))
    deboneInventoryShowNoRecordsHint.value = deboneInventoryRows.value.length === 0
  } catch (err) {
    console.error(err)
    deboneInventoryError.value = err?.message || 'Failed to load debone inventory'
    deboneInventoryRows.value = []
    deboneInventoryShowNoRecordsHint.value = false
  } finally {
    isLoadingDeboneInventory.value = false
  }
}

async function handleAllowDeboneInventoryEditAccess() {
  if (isSubmittingDeboneInventoryEditAccess.value) return
  if (!deboneInventoryRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!calendarDate.value) {
    alert('Please select Calendar')
    return
  }

  const selectedRows = deboneInventoryRows.value.filter(
    (r) => !!(r.qtyPcSelected || r.qtyKgLSelected || r.remarkSelected)
  )
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Qty PC, Qty Kg/L, Remark).')
    return
  }

  const missingId = selectedRows.filter((r) => r.deboneInventoryId == null || !Number.isFinite(Number(r.deboneInventoryId)))
  if (missingId.length > 0) {
    alert('Some rows are missing the saved inventory id. Reload Debone Inventory (Find) and try again.')
    return
  }

  const rows = selectedRows.map((r) => ({
    deboneInventoryId: Number(r.deboneInventoryId),
    departmentId: editAccessDepartmentIdForPayload(),
    calendar: calendarDate.value,
    type: r.type || 'item',
    itemId: r.type === 'item' ? (r.itemId ?? null) : null,
    deboneRegistrationId: r.type === 'debone' ? (r.deboneRegistrationId ?? null) : null,
    qty_pc: !!r.qtyPcSelected,
    qty_kgl: !!r.qtyKgLSelected,
    remark: !!r.remarkSelected,
  }))

  try {
    isSubmittingDeboneInventoryEditAccess.value = true
    const res = await apiFetch('/edit-access/debone-inventory', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request debone inventory edit access')
    }
    alert('Debone inventory edit access requests submitted successfully')
    deboneInventoryRows.value = []
    deboneInventoryShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit debone inventory edit access request')
  } finally {
    isSubmittingDeboneInventoryEditAccess.value = false
  }
}

async function loadDeboneControlsForEditAccess() {
  const cal = calendarDate.value
  if (!cal) {
    deboneControlsError.value = 'Please select Calendar.'
    deboneControlsRows.value = []
    deboneControlsShowNoRecordsHint.value = false
    return
  }

  isLoadingDeboneControls.value = true
  deboneControlsError.value = ''
  deboneControlsShowNoRecordsHint.value = false
  try {
    const deptParam = encodeURIComponent(editAccessDepartmentQueryValue())
    const res = await apiFetch(`/debones/controls/saved?departmentId=${deptParam}&calendar=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load debone controls')
    }
    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    const savedRows = Array.isArray(payload?.rows) ? payload.rows : []

    if (!payload?.hasData || savedRows.length === 0) {
      deboneControlsRows.value = []
      deboneControlsShowNoRecordsHint.value = true
      return
    }

    deboneControlsRows.value = savedRows
      .map((row) => ({
        ...row,
        deboneControlId: row.deboneControlId ?? row.id ?? null,
        type: row.type || row.kind || 'item',
        ingredientName: row.ingredientName || '-',
        wastagePc: Number(row.wastagePc ?? 0),
        wastageKgL: Number(row.wastageKgL ?? 0),
        totalDistributedPc: Number(row.totalDistributedPc ?? 0),
        totalDistributedKgL: Number(row.totalDistributedKgL ?? 0),
        spoiledItemPc: Number(row.spoiledItemPc ?? 0),
        spoiledItemKgL: Number(row.spoiledItemKgL ?? 0),
        cateringPc: Number(row.cateringPc ?? 0),
        cateringKgL: Number(row.cateringKgL ?? 0),
        itemId: row.itemId ?? null,
        deboneRegistrationId: row.deboneRegistrationId ?? null,
        wastagePcSelected: false,
        wastageKgLSelected: false,
        sendToDSelected: false,
        spoiledItemPcSelected: false,
        spoiledItemKgLSelected: false,
        cateringPcSelected: false,
        cateringKgLSelected: false,
      }))
      .sort((a, b) => String(a.ingredientName || '').localeCompare(String(b.ingredientName || '')))
    deboneControlsShowNoRecordsHint.value = deboneControlsRows.value.length === 0
  } catch (err) {
    console.error(err)
    deboneControlsError.value = err?.message || 'Failed to load debone controls'
    deboneControlsRows.value = []
    deboneControlsShowNoRecordsHint.value = false
  } finally {
    isLoadingDeboneControls.value = false
  }
}

async function handleAllowDeboneControlsEditAccess() {
  if (isSubmittingDeboneControlsEditAccess.value) return
  if (!deboneControlsRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!calendarDate.value) {
    alert('Please select Calendar')
    return
  }

  const selectedRows = deboneControlsRows.value.filter((r) => !!(
    r.wastagePcSelected ||
    r.wastageKgLSelected ||
    r.sendToDSelected ||
    r.spoiledItemPcSelected ||
    r.spoiledItemKgLSelected ||
    r.cateringPcSelected ||
    r.cateringKgLSelected
  ))
  if (selectedRows.length === 0) {
    alert('Please select at least one editable field.')
    return
  }

  const missingId = selectedRows.filter((r) => r.deboneControlId == null || !Number.isFinite(Number(r.deboneControlId)))
  if (missingId.length > 0) {
    alert('Some rows are missing the saved control id. Reload Debone Controls (Find) and try again.')
    return
  }

  const rows = selectedRows.map((r) => ({
    deboneControlId: Number(r.deboneControlId),
    departmentId: editAccessDepartmentIdForPayload(),
    calendar: calendarDate.value,
    type: r.type || 'item',
    itemId: r.type === 'item' ? (r.itemId ?? null) : null,
    deboneRegistrationId: r.type === 'debone' ? (r.deboneRegistrationId ?? null) : null,
    wastage_pc: !!r.wastagePcSelected,
    wastage_kgl: !!r.wastageKgLSelected,
    send_to_d: !!r.sendToDSelected,
    spoiled_item_pc: !!r.spoiledItemPcSelected,
    spoiled_item_kgl: !!r.spoiledItemKgLSelected,
    catering_pc: !!r.cateringPcSelected,
    catering_kgl: !!r.cateringKgLSelected,
  }))

  try {
    isSubmittingDeboneControlsEditAccess.value = true
    const res = await apiFetch('/edit-access/debone-controls', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request debone controls edit access')
    }
    alert('Debone controls edit access requests submitted successfully')
    deboneControlsRows.value = []
    deboneControlsShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit debone controls edit access request')
  } finally {
    isSubmittingDeboneControlsEditAccess.value = false
  }
}

async function loadConsumption() {
  const deptId = selectedDepartment.value
  const cal = calendarDate.value
  if (!deptId || !cal) {
    consumptionError.value = 'Please select Department and Calendar.'
    consumptionRows.value = []
    consumptionShowNoRecordsHint.value = false
    return
  }

  isLoadingConsumption.value = true
  consumptionError.value = ''
  consumptionShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(`/consumption/saved?departmentId=${deptId}&calendar=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load consumption')
    }
    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    const raw = Array.isArray(payload?.rows) ? payload.rows : []

    if (!payload?.hasData || raw.length === 0) {
      consumptionRows.value = []
      consumptionShowNoRecordsHint.value = true
      return
    }

    consumptionRows.value = raw.map((row) => ({
      ...row,
      consumptionRecordId: row.consumptionRecordId ?? row.id ?? null,
      type: row.type || 'item',
      itemId: row.itemId ?? row.item_id ?? null,
      productId: row.productId ?? row.product_id ?? null,
      ingredient: row.ingredient || row.ingredientName || '-',
      bbf: Number(row.bbf ?? 0),
      storeRecv: Number(row.storeRecv ?? 0),
      nonStock: Number(row.nonStock ?? 0),
      production: Number(row.production ?? 0),
      transIn1: Number(row.transIn1 ?? 0),
      transIn2: Number(row.transIn2 ?? 0),
      transInDetails: Array.isArray(row.transInDetails) ? row.transInDetails : [],
      wastage: Number(row.wastage ?? 0),
      totalAvail: Number(String(row.totalAvail ?? 0).replace(/,/g, '')) || 0,
      salesManual: Number(row.salesManual ?? 0),
      salesSystem: Number(row.salesSystem ?? 0),
      mgmt: Number(row.mgmt ?? 0),
      room: Number(row.room ?? 0),
      promo: Number(row.promo ?? 0),
      coCost: Number(row.coCost ?? 0),
      spoilage: Number(row.spoilage ?? 0),
      other: Number(row.other ?? 0),
      transOut1: Number(row.transOut1 ?? 0),
      transOut2: Number(row.transOut2 ?? 0),
      transOutDetails: Array.isArray(row.transOutDetails) ? row.transOutDetails : [],
      spoiledItem: Number(row.spoiledItem ?? 0),
      catering: Number(row.catering ?? 0),
      totalUsed: Number(String(row.totalUsed ?? 0).replace(/,/g, '')) || 0,
      balance: Number(String(row.balance ?? 0).replace(/,/g, '')) || 0,
      ending: Number(row.ending ?? 0),
      variance: Number(String(row.variance ?? 0).replace(/,/g, '')) || 0,
      wastageSelected: false,
      transferIssueDSelected: false,
      spoiledItemSelected: false,
      cateringSelected: false,
    })).sort((a, b) => String(a.ingredient || '').localeCompare(String(b.ingredient || '')))
    consumptionShowNoRecordsHint.value = consumptionRows.value.length === 0
  } catch (err) {
    console.error(err)
    consumptionError.value = err?.message || 'Failed to load consumption'
    consumptionRows.value = []
    consumptionShowNoRecordsHint.value = false
  } finally {
    isLoadingConsumption.value = false
  }
}

async function handleAllowConsumptionEditAccess() {
  if (isSubmittingConsumptionEditAccess.value) return
  if (!consumptionRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!selectedDepartment.value || !calendarDate.value) {
    alert('Please select Department and Calendar')
    return
  }

  const selectedRows = consumptionRows.value.filter(r => !!(
    r.wastageSelected ||
    r.transferIssueDSelected ||
    r.spoiledItemSelected ||
    r.cateringSelected
  ))
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Wastage, Transfer Issu (D), Spoiled By Items, Other/Catering).')
    return
  }

  const rows = selectedRows.map((r) => ({
    consumptionRecordId: r.consumptionRecordId ?? r.id ?? null,
    departmentId: selectedDepartment.value,
    calendar: calendarDate.value,
    type: r.type || null,
    itemId: r.type === 'item' ? (r.itemId ?? null) : null,
    productId: r.type === 'product' ? (r.productId ?? null) : null,
    wastage: !!r.wastageSelected,
    transfer_issue_d: !!r.transferIssueDSelected,
    spoiled_item: !!r.spoiledItemSelected,
    catering: !!r.cateringSelected,
  }))

  try {
    isSubmittingConsumptionEditAccess.value = true
    const res = await apiFetch('/edit-access/consumption', {
      method: 'POST',
      body: JSON.stringify({ rows })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request consumption edit access')
    }
    alert('Consumption edit access requests submitted successfully')
    consumptionRows.value = []
    consumptionShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit consumption edit access request')
  } finally {
    isSubmittingConsumptionEditAccess.value = false
  }
}

async function loadMaterialInventoryForEditAccess() {
  const deptId = selectedDepartment.value
  const cal = calendarDate.value
  if (!deptId || !cal) {
    materialInventoryError.value = 'Please select Department and Calendar.'
    materialInventoryRows.value = []
    materialInventoryShowNoRecordsHint.value = false
    return
  }

  isLoadingMaterialInventory.value = true
  materialInventoryError.value = ''
  materialInventoryShowNoRecordsHint.value = false
  try {
    const res = await apiFetch(`/material-selection/inventory/report?departmentId=${deptId}&selectedDate=${cal}`)
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to load material inventory')
    }
    const payload = await res.json().catch(() => ({ rows: [] }))
    const rows = Array.isArray(payload) ? payload : (payload?.rows || payload?.data || [])
    materialInventoryRows.value = (Array.isArray(rows) ? rows : []).map((r) => ({
      ...r,
      materialInventoryId: r.id ?? null,
      itemId: r.itemId ?? r.item_id ?? null,
      itemCode: r.itemCode || '-',
      categoryName: r.categoryName || '-',
      itemName: r.itemName || '-',
      uomStock: r.uomStock || '-',
      lastCount: Number(r.lastCount ?? 0),
      lastCountDate: r.selectedDate ?? calendarDate.value,
      received: Number(r.received ?? 0),
      currentBalance: Number(r.currentBalance ?? 0),
      newQty: Number(r.newQty ?? 0),
      difference: Number(r.difference ?? 0),
      newQtySelected: false,
    }))
      .sort((a, b) => String(a.itemName || '').localeCompare(String(b.itemName || '')))
    materialInventoryShowNoRecordsHint.value = materialInventoryRows.value.length === 0
  } catch (err) {
    console.error(err)
    materialInventoryError.value = err?.message || 'Failed to load material inventory'
    materialInventoryRows.value = []
    materialInventoryShowNoRecordsHint.value = false
  } finally {
    isLoadingMaterialInventory.value = false
  }
}

async function handleAllowMaterialInventoryEditAccess() {
  if (isSubmittingMaterialInventoryEditAccess.value) return
  if (!materialInventoryRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!selectedDepartment.value || !calendarDate.value) {
    alert('Please select Department and Calendar')
    return
  }

  const selectedRows = materialInventoryRows.value.filter(r => !!r.newQtySelected)
  if (selectedRows.length === 0) {
    alert('Please select New Qty.')
    return
  }

  const rows = selectedRows.map((r) => ({
    materialInventoryId: r.materialInventoryId ?? null,
    departmentId: selectedDepartment.value,
    selectedDate: calendarDate.value,
    itemId: r.itemId ?? null,
    new_qty: !!r.newQtySelected,
  }))

  try {
    isSubmittingMaterialInventoryEditAccess.value = true
    const res = await apiFetch('/edit-access/material-inventory', {
      method: 'POST',
      body: JSON.stringify({ rows })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request material inventory edit access')
    }
    alert('Material inventory edit access requests submitted successfully')
    materialInventoryRows.value = []
    materialInventoryShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit material inventory edit access request')
  } finally {
    isSubmittingMaterialInventoryEditAccess.value = false
  }
}

async function handleAllowDailyProductsEditAccess() {
  if (isSubmittingDailyProdEditAccess.value) return
  if (!dailyProductsRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!selectedDepartment.value || !calendarDate.value) {
    alert('Please select Department and Calendar')
    return
  }

  const selectedRows = dailyProductsRows.value.filter(r => !!r.quantitySelected)
  if (selectedRows.length === 0) {
    alert('Please select Quantity.')
    return
  }

  const rows = selectedRows.map((r) => ({
    departmentId: selectedDepartment.value,
    calendar: calendarDate.value,
    productionId: r.productionId ?? null,
    productId: r.productId ?? null,
    quantity: true,
  }))

  try {
    isSubmittingDailyProdEditAccess.value = true
    const res = await apiFetch('/edit-access/daily-production', {
      method: 'POST',
      body: JSON.stringify({ rows })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request daily products edit access')
    }
    alert('Daily products edit access requests submitted successfully')
    dailyProductsRows.value = []
    dailyProductsShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit daily products edit access request')
  } finally {
    isSubmittingDailyProdEditAccess.value = false
  }
}

async function handleAllowDailyTransactionEditAccess() {
  if (isSubmittingDailyTransEditAccess.value) return
  if (!dailyTransactionRows.value.length) {
    alert('No items to process.')
    return
  }
  if (!selectedDepartment.value || !calendarDate.value) {
    alert('Please select Department and Calendar')
    return
  }

  const selectedRows = dailyTransactionRows.value.filter(r => !!(
    r.salesManualSelected ||
    r.managementSelected ||
    r.roomSelected ||
    r.promotionSelected ||
    r.companyCostSelected ||
    r.spoilageSelected ||
    r.otherSelected
  ))
  if (selectedRows.length === 0) {
    alert('Please select at least one field (Sales Man, Mgmt, Room, Promo, Co. Cost, Spoilage, Other).')
    return
  }

  const rows = selectedRows.map((r) => ({
    departmentId: selectedDepartment.value,
    calendar: calendarDate.value,
    dailyTransactionId: r.dailyTransactionId ?? null,
    menuId: r.menuId ?? null,
    sales_manual: !!r.salesManualSelected,
    management: !!r.managementSelected,
    room: !!r.roomSelected,
    promotion: !!r.promotionSelected,
    company_cost: !!r.companyCostSelected,
    spoilage: !!r.spoilageSelected,
    other: !!r.otherSelected,
  }))

  try {
    isSubmittingDailyTransEditAccess.value = true
    const res = await apiFetch('/edit-access/daily-transaction', {
      method: 'POST',
      body: JSON.stringify({ rows })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request daily transaction edit access')
    }
    alert('Daily transaction edit access requests submitted successfully')
    dailyTransactionRows.value = []
    dailyTransactionShowNoRecordsHint.value = false
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit daily transaction edit access request')
  } finally {
    isSubmittingDailyTransEditAccess.value = false
  }
}

async function handleFind() {
  if (selectedProduction.value) {
    if (!calendarDate.value) {
      alert('Please select Calendar')
      return
    }
    if (isDeboneProductionSelected.value) {
      if (selectedProduction.value === 'Debone Inventory') {
        await loadDeboneInventoryForEditAccess()
        return
      }
      if (selectedProduction.value === 'Debone Controls') {
        await loadDeboneControlsForEditAccess()
        return
      }
    }
    if (isCentralKitchenProductionSelected.value) {
      if (selectedProduction.value === 'Central Inventory') {
        await loadCentralInventory()
        return
      }
      if (selectedProduction.value === 'Central Product Distribution') {
        await loadProductDistributionForEditAccess()
        return
      }
      if (selectedProduction.value === 'Central Item Controls') {
        await loadCentralItemControlForEditAccess()
        return
      }
      alert('This central kitchen production type is not implemented yet.')
      return
    }
    if (!selectedDepartment.value) {
      alert('Please select Department and Calendar')
      return
    }
    if (selectedProduction.value === 'Daily Inventory') {
      await loadDailyInventory()
      return
    }
    if (selectedProduction.value === 'Daily products') {
      await loadDailyProducts()
      return
    }
    if (selectedProduction.value === 'Daily Transaction') {
      await loadDailyTransaction()
      return
    }
    if (selectedProduction.value === 'Consumption') {
      await loadConsumption()
      return
    }
    if (selectedProduction.value === 'Material Inventory') {
      await loadMaterialInventoryForEditAccess()
      return
    }
    alert('This production type is not implemented yet.')
    return
  }

  const grvNo = String(grvNumber.value || '').trim()
  const sivNo = String(sivNumber.value || '').trim()
  const nsNo = String(nsNumber.value || '').trim()
  hasSearched.value = false
  grvRows.value = []
  sivRows.value = []
  nsRows.value = []

  if (!grvNo && !sivNo && !nsNo) {
    alert('Please enter GRV Number, SIV Number or NS Number')
    return
  }

  isFinding.value = true
  try {
    const baseQueryParts = []
    if (branchId.value) baseQueryParts.push(`branchId=${branchId.value}`)
    else if (companyId.value) baseQueryParts.push(`companyId=${companyId.value}`)

    const fetches = []
    if (grvNo) {
      const qp = [...baseQueryParts, `grvNo=${encodeURIComponent(grvNo)}`]
      fetches.push({ kind: 'grv', promise: apiFetch(`/grv/report?${qp.join('&')}`) })
    }
    if (sivNo) {
      const qp = [...baseQueryParts, `sivNo=${encodeURIComponent(sivNo)}`]
      fetches.push({ kind: 'siv', promise: apiFetch(`/siv/report?${qp.join('&')}`) })
    }
    if (nsNo) {
      const qp = [...baseQueryParts]
      fetches.push({ kind: 'ns', promise: apiFetch(`/non-stockable?${qp.join('&')}`) })
    }

    const results = await Promise.all(fetches.map(f => f.promise.then(res => ({ kind: f.kind, res }))))

    const grvResult = results.find(r => r.kind === 'grv')
    const sivResult = results.find(r => r.kind === 'siv')
    const nsResult = results.find(r => r.kind === 'ns')

    // ---- GRV ----
    if (grvResult) {
      const res = grvResult.res
      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.message || 'Failed to fetch GRV items')
      }

      const data = await res.json()
      const grvs = Array.isArray(data) ? data : (data?.data || [])

      // If some items are missing category info but have item_id/itemId, load lookups once.
      let needsCategoryLookup = false
      for (const g of grvs) {
        const items = Array.isArray(g?.items) ? g.items : []
        for (const it of items) {
          const itemId = it?.item_id ?? it?.itemId ?? it?.itemID ?? null
          const hasCategory = pickCategoryName(it) && pickCategoryName(it) !== '-'
          if (!hasCategory && itemId != null) {
            needsCategoryLookup = true
            break
          }
        }
        if (needsCategoryLookup) break
      }
      if (needsCategoryLookup) {
        await ensureItemAndCategoryLookups()
      }

      const rows = []
      for (const g of grvs) {
        const transactionDateRaw = g?.date ?? g?.transactionDate ?? g?.tr?.date ?? g?.created_at ?? null
        const baseHeader = {
          grvNo: g?.grvNo || g?.grvNumber || grvNo,
          transactionDateRaw,
          transactionDate: transactionDateRaw ? toYmd(transactionDateRaw) : '-',
          prNo: g?.prNumber || g?.prNo || '-',
          supplier: g?.supplier?.name || g?.supplierName || '-',
        }

        const items = Array.isArray(g?.items) ? g.items : []
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
          const it = items[itemIdx]
          const grvItemId = it?.id ?? it?.grvItemId ?? it?.grv_item_id ?? null
          const header = {
            ...baseHeader,
            prNo: it?.prNumber || it?.pr_no || baseHeader.prNo || '-',
          }
          const itemId = it?.item_id ?? it?.itemId ?? it?.itemID ?? it?.item?.id ?? null
          const sourceItemKey = `${String(grvItemId ?? g?.grvNo ?? grvNo)}-${String(itemId ?? 'no-item')}-${itemIdx}`
          const categoryFromPayload = pickCategoryName(it)
          const category = (categoryFromPayload && categoryFromPayload !== '-') ? categoryFromPayload : getCategoryNameFromItemId(itemId)
          const baseRows = expandGrvItemToRows(header, it)
          for (const r of baseRows) {
            rows.push({
              ...r,
              grvId: grvItemId,
              sourceItemKey,
              itemId,
              category,
              item: pickItemName(it),
              prNoSelected: false,
              supplierSelected: false,
              categorySelected: false,
              itemSelected: false,
              qtySelected: false,
              volumeSelected: false,
              transactionDateSelected: false,
              expirySelected: false,
              taxableSelected: false,
            })
          }
        }
      }

      grvRows.value = rows
    }

    // ---- SIV ----
    if (sivResult) {
      const res = sivResult.res
      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.message || 'Failed to fetch SIV items')
      }

      const data = await res.json()
      const sivs = Array.isArray(data) ? data : (data?.data || [])

      let needsDepartmentLookup = false
      for (const s of sivs) {
        const depName = s?.department?.name || s?.departmentName || s?.department?.departmentName
        const depId = s?.departmentId ?? s?.department_id ?? null
        if ((!depName || depName === '-') && depId != null) {
          needsDepartmentLookup = true
          break
        }
      }
      if (needsDepartmentLookup) {
        await ensureDepartmentLookups()
      }

      let needsCategoryLookup = false
      for (const s of sivs) {
        const items = Array.isArray(s?.items) ? s.items : []
        for (const it of items) {
          const itemId = it?.item_id ?? it?.itemId ?? it?.itemID ?? it?.item?.id ?? null
          const hasCategory = pickCategoryName(it) && pickCategoryName(it) !== '-'
          if (!hasCategory && itemId != null) {
            needsCategoryLookup = true
            break
          }
        }
        if (needsCategoryLookup) break
      }
      if (needsCategoryLookup) {
        await ensureItemAndCategoryLookups()
      }

      const rows = []
      for (const s of sivs) {
        const transactionDateRaw = s?.date ?? s?.transactionDate ?? s?.created_at ?? null
        const baseHeader = {
          sivNo: s?.sivNo || sivNo,
          transactionDateRaw,
          transactionDate: transactionDateRaw ? toYmd(transactionDateRaw) : '-',
          department: pickDepartmentName(s),
          remark: s?.remark ?? '-',
        }

        const items = Array.isArray(s?.items) ? s.items : []
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
          const it = items[itemIdx]
          const sivItemId = it?.id ?? it?.sivItemId ?? it?.siv_item_id ?? null
        const rawItemId = it?.item_id ?? it?.itemId ?? it?.itemID ?? it?.item?.id ?? null
        const rawProductId = it?.product_id ?? it?.productId ?? it?.productID ?? it?.product?.id ?? null
        // Requirement: "item" field should carry the id for both item and product.
        const itemId = rawItemId != null ? rawItemId : rawProductId
          const sourceItemKey = `${String(sivItemId ?? s?.sivNo ?? sivNo)}-${String(itemId ?? 'no-item')}-${itemIdx}`
          const categoryFromPayload = pickCategoryName(it)
          const category = it?.productId
            ? '-' // Requirement: product rows show Category as "-"
            : ((categoryFromPayload && categoryFromPayload !== '-') ? categoryFromPayload : getCategoryNameFromItemId(itemId))

          const baseRows = expandSivItemToRows(baseHeader, it)
          for (const r of baseRows) {
            rows.push({
              ...r,
              sivId: sivItemId,
              sourceItemKey,
              itemId,
              department: pickSivRowDepartmentName(s),
              category,
              item: pickItemName(it),
              transactionDateSelected: false,
              departmentSelected: false,
              categorySelected: false,
              itemSelected: false,
              qtySelected: false,
              volumeSelected: false,
              remarkSelected: false,
            })
          }
        }
      }

      sivRows.value = rows
    }

    // ---- Non-Stockable ----
    if (nsResult) {
      const res = nsResult.res
      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.message || 'Failed to fetch Non-Stockable items')
      }

      const data = await res.json()
      const allNs = Array.isArray(data) ? data : (data?.data || [])
      const nsList = allNs.filter(n => String(n?.nsNo || '').toLowerCase().includes(nsNo.toLowerCase()))

      let needsDepartmentLookup = false
      for (const n of nsList) {
        const depName = n?.department?.name || n?.departmentName || n?.department?.departmentName
        const depId = n?.departmentId ?? n?.department_id ?? null
        if ((!depName || depName === '-') && depId != null) {
          needsDepartmentLookup = true
          break
        }
      }
      if (needsDepartmentLookup) {
        await ensureDepartmentLookups()
      }

      const rows = []
      for (const n of nsList) {
        const transactionDateRaw = n?.date ?? n?.transactionDate ?? n?.created_at ?? null
        const baseHeader = {
          nsNo: n?.nsNo || nsNo,
          transactionDateRaw,
          transactionDate: transactionDateRaw ? toYmd(transactionDateRaw) : '-',
          prNo: n?.prNo || '-',
          supplier: n?.supplier?.name || n?.supplierName || '-',
          department: n?.department?.name || n?.departmentName || pickDepartmentName(n),
          category: n?.category?.name || n?.categoryName || '-',
        }

        const items = Array.isArray(n?.items) ? n.items : []
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
          const it = items[itemIdx]
          const nsItemId = it?.id ?? it?.nsItemId ?? it?.nonStockableItemId ?? null
          const rawItemId = it?.item_id ?? it?.itemId ?? it?.item?.id ?? null
          const rawProductId = it?.product_id ?? it?.productId ?? it?.product?.id ?? null
          const itemId = rawItemId != null ? rawItemId : rawProductId
          const sourceItemKey = `${String(nsItemId ?? n?.nsNo ?? nsNo)}-${String(itemId ?? 'no-item')}-${itemIdx}`

          const baseRows = expandNsItemToRows(baseHeader, it)
          for (const r of baseRows) {
            rows.push({
              ...r,
              nsId: nsItemId,
              sourceItemKey,
              itemId,
              item: pickItemName(it),
              prNoSelected: false,
              supplierSelected: false,
              departmentSelected: false,
              categorySelected: false,
              itemSelected: false,
              qtySelected: false,
              volumeSelected: false,
              transactionDateSelected: false,
              taxableSelected: false,
            })
          }
        }
      }

      nsRows.value = rows
    }

    hasSearched.value = true
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to fetch items')
    hasSearched.value = true
  } finally {
    isFinding.value = false
  }
}

async function handleAllowSivEditAccess() {
  if (isSubmittingEditAccess.value) return

  if (sivRows.value.length === 0) {
    alert('No items to process.')
    return
  }

  const grouped = new Map()
  for (const r of sivRows.value) {
    const key = r.sourceItemKey || `${String(r.sivId ?? '')}-${String(r.itemId ?? '')}-${String(r.sivNo ?? '')}`
    const existing = grouped.get(key)
    if (!existing) {
      grouped.set(key, {
        sivId: r.sivId != null ? Number(r.sivId) : null,
        sivNo: r.sivNo,
        item: r.itemId != null ? String(r.itemId) : null,
        transaction_date: !!r.transactionDateSelected,
        department: !!r.departmentSelected,
        category: !!r.categorySelected,
        item_field: !!r.itemSelected,
        remark: !!r.remarkSelected,
        _quantityDetailFlags: [!!r.qtySelected],
        _volumeDetailFlags: [!!r.volumeSelected],
      })
      continue
    }

    existing.transaction_date = existing.transaction_date || !!r.transactionDateSelected
    existing.department = existing.department || !!r.departmentSelected
    existing.category = existing.category || !!r.categorySelected
    existing.item_field = existing.item_field || !!r.itemSelected
    existing.remark = existing.remark || !!r.remarkSelected
    existing._quantityDetailFlags.push(!!r.qtySelected)
    existing._volumeDetailFlags.push(!!r.volumeSelected)
  }

  const rows = Array.from(grouped.values()).map((g) => ({
    sivId: g.sivId,
    sivNo: g.sivNo,
    item: g.item,
    transaction_date: g.transaction_date,
    department: g.department,
    category: g.category,
    item_field: g.item_field,
    remark: g.remark,
    quantity_details: g._quantityDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
    volume_details: g._volumeDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
  }))

  const created_by = currentUser.value?.id ?? currentUser.value?.userId ?? null

  try {
    isSubmittingEditAccess.value = true
    const res = await apiFetch('/edit-access/siv', {
      method: 'POST',
      body: JSON.stringify({ rows, created_by })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request edit access')
    }

    alert('Edit access requests submitted successfully')
    sivRows.value = []
    hasSearched.value = false
    sivNumber.value = ''
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit edit access request')
  } finally {
    isSubmittingEditAccess.value = false
  }
}

async function handleAllowNsEditAccess() {
  if (isSubmittingEditAccess.value) return

  if (nsRows.value.length === 0) {
    alert('No items to process.')
    return
  }

  const grouped = new Map()
  for (const r of nsRows.value) {
    const key = r.sourceItemKey || `${String(r.nsId ?? '')}-${String(r.itemId ?? '')}-${String(r.nsNo ?? '')}`
    const existing = grouped.get(key)
    if (!existing) {
      grouped.set(key, {
        nsId: r.nsId != null ? Number(r.nsId) : null,
        nsNo: r.nsNo,
        item: r.itemId != null ? String(r.itemId) : null,
        prNo: !!r.prNoSelected,
        supplier: !!r.supplierSelected,
        department: !!r.departmentSelected,
        category: !!r.categorySelected,
        item_field: !!r.itemSelected,
        qty: !!r.qtySelected,
        volume: !!r.volumeSelected,
        transaction_date: !!r.transactionDateSelected,
        taxable: !!r.taxableSelected,
        _quantityDetailFlags: [!!r.qtySelected],
        _volumeDetailFlags: [!!r.volumeSelected],
      })
      continue
    }

    existing.prNo = existing.prNo || !!r.prNoSelected
    existing.supplier = existing.supplier || !!r.supplierSelected
    existing.department = existing.department || !!r.departmentSelected
    existing.category = existing.category || !!r.categorySelected
    existing.item_field = existing.item_field || !!r.itemSelected
    existing.qty = existing.qty || !!r.qtySelected
    existing.volume = existing.volume || !!r.volumeSelected
    existing.transaction_date = existing.transaction_date || !!r.transactionDateSelected
    existing.taxable = existing.taxable || !!r.taxableSelected
    existing._quantityDetailFlags.push(!!r.qtySelected)
    existing._volumeDetailFlags.push(!!r.volumeSelected)
  }

  const rows = Array.from(grouped.values()).map((g) => ({
    nsId: g.nsId,
    nsNo: g.nsNo,
    item: g.item,
    prNo: g.prNo,
    supplier: g.supplier,
    department: g.department,
    category: g.category,
    item_field: g.item_field,
    qty: g.qty,
    volume: g.volume,
    transaction_date: g.transaction_date,
    taxable: g.taxable,
    quantity_details: g._quantityDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
    volume_details: g._volumeDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
  }))
  const created_by = currentUser.value?.id ?? currentUser.value?.userId ?? null

  try {
    isSubmittingEditAccess.value = true
    const res = await apiFetch('/edit-access/ns', {
      method: 'POST',
      body: JSON.stringify({ rows, created_by })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request edit access')
    }

    alert('Edit access requests submitted successfully')
    nsRows.value = []
    hasSearched.value = false
    nsNumber.value = ''
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit edit access request')
  } finally {
    isSubmittingEditAccess.value = false
  }
}

async function handleAllowEditAccess() {
  if (isSubmittingEditAccess.value) return

  if (grvRows.value.length === 0) {
    alert('No items to process.')
    return
  }

  const grouped = new Map()
  for (const r of grvRows.value) {
    const key = r.sourceItemKey || `${String(r.grvId ?? '')}-${String(r.itemId ?? '')}-${String(r.grvNo ?? '')}`
    const existing = grouped.get(key)
    if (!existing) {
      grouped.set(key, {
        grvId: r.grvId != null ? Number(r.grvId) : null,
        grvNo: r.grvNo,
        item: r.itemId != null ? String(r.itemId) : null,
        prNo: !!r.prNoSelected,
        supplier: !!r.supplierSelected,
        category: !!r.categorySelected,
        item_field: !!r.itemSelected,
        qty: !!r.qtySelected,
        volume: !!r.volumeSelected,
        transaction_date: !!r.transactionDateSelected,
        _quantityDetailFlags: [!!r.qtySelected],
        _volumeDetailFlags: [!!r.volumeSelected],
        _expiryDetailFlags: [!!r.expirySelected],
        expiry: !!r.expirySelected,
        taxable: !!r.taxableSelected
      })
      continue
    }

    existing.prNo = existing.prNo || !!r.prNoSelected
    existing.supplier = existing.supplier || !!r.supplierSelected
    existing.category = existing.category || !!r.categorySelected
    existing.item_field = existing.item_field || !!r.itemSelected
    existing.qty = existing.qty || !!r.qtySelected
    existing.volume = existing.volume || !!r.volumeSelected
    existing.transaction_date = existing.transaction_date || !!r.transactionDateSelected
    existing._quantityDetailFlags.push(!!r.qtySelected)
    existing._volumeDetailFlags.push(!!r.volumeSelected)
    existing._expiryDetailFlags.push(!!r.expirySelected)
    existing.expiry = existing.expiry || !!r.expirySelected
    existing.taxable = existing.taxable || !!r.taxableSelected
  }
  const rows = Array.from(grouped.values()).map((g) => ({
    grvId: g.grvId,
    grvNo: g.grvNo,
    item: g.item,
    prNo: g.prNo,
    supplier: g.supplier,
    category: g.category,
    item_field: g.item_field,
    qty: g.qty,
    volume: g.volume,
    transaction_date: g.transaction_date,
    quantity_details: g._quantityDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
    volume_details: g._volumeDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
    expiry_date_details: g._expiryDetailFlags.map(v => (v ? 'true' : 'false')).join(','),
    expiry: g.expiry,
    taxable: g.taxable
  }))
  const created_by = currentUser.value?.id ?? currentUser.value?.userId ?? null

  try {
    isSubmittingEditAccess.value = true
    const res = await apiFetch('/edit-access/grv', {
      method: 'POST',
      body: JSON.stringify({ rows, created_by })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => null)
      throw new Error(err?.message || 'Failed to request edit access')
    }

    alert('Edit access requests submitted successfully')
    grvRows.value = []
    hasSearched.value = false
    grvNumber.value = ''
  } catch (err) {
    console.error(err)
    alert(err?.message || 'Failed to submit edit access request')
  } finally {
    isSubmittingEditAccess.value = false
  }
}
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.65rem;
  color: #1e293b;
}

th,
td {
  padding: 3px 4px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.65rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.status-pill {
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: bold;
  display: inline-block;
}
.active {
  background: #dcfce7;
  color: #166534;
}
.inactive {
  background: #f1f5f9;
  color: red;
}
</style>

